/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {error} from '@ion/core/src/logger/logger';
import {
  registerToken
} from '@ion/core/src/services/Reports';
import {DashboardId} from '@ion/core/src/services/Sisense';
import {PageState} from '@/types/pages';
import {getGridColumnState} from '../../helpers';

export const INITIALIZE_REPORTS             = 'INITIALIZE_REPORTS';

// retrieve jasper options ...
export const REGISTER_TOKEN_SUCCESS           = 'REGISTER_TOKEN_SUCCESS';

// Sisense repated actions
export const SET_SISENSE_DEFAULT_DASHBOARD = 'SET_SISENSE_DEFAULT_DASHBOARD';

// name of the reducer
export const NAME = 'reports';

export enum ReportsMode {
  ReportsDashboard = 'ReportsDashboard'
};

/**
 * initialize
 */
export const initializeReports = () => ({
  type : INITIALIZE_REPORTS
});

/**
 * This function checks if the user has access to Dashboard or Reports. If the user
 * has access, it registers the token and dispatches actions accordingly.
 *
 *  ```typescript
 *
 *  import {retrieveReportOptions} from '../../store/actions/reports';
 *
 *  const dispatch = useDispatch();
 *  dispatch(retrieveReportOptions());
 *
 * ```
 */
export const retrieveReportOptions = (isPreValidated: boolean = false, param?: any) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const {
      reports: {
        isTokenRegistered
      }
    } = getState();
    if (isPreValidated || param?.Features.hasFeature(param?.DASHBOARD_PAGE, param?.REPORTS)) {
      try {
        if (!isTokenRegistered) {
          await registerToken();
          dispatch({type: REGISTER_TOKEN_SUCCESS});
        }
      } catch (err) {
        error(err);
        return err;
      }
    }
  };
};

export const setSisenseDefaultDashboard = (id: null | DashboardId) => ({
  type : SET_SISENSE_DEFAULT_DASHBOARD,
  id
});

export interface ReportsState extends PageState {
  isInitialized: boolean;
  isTokenRegistered: boolean;
  sisense: {
    defaultDashboard: DashboardId
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean
  }
}

const PREFERENCE = 'reports';

// defines the initial state for the reducer ...
const initialState: ReportsState = {
  isInitialized     : false,
  isTokenRegistered : false,
  sisense           : {
    defaultDashboard : null
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false
  }
};

export interface ReportsReducerState {
  reports: ReportsState;
};

// defines the reducers ...
const reducers = {
  [INITIALIZE_REPORTS] : draft => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
  },
  [SET_SISENSE_DEFAULT_DASHBOARD] : ({sisense}: ReportsState, {id}) => {
    sisense.defaultDashboard = id;
  },
  [REGISTER_TOKEN_SUCCESS] : draft => {
    draft.isTokenRegistered = true;
  }
};

// defines all reducers for actions of interest to the this reducer ...
export default produce((draft: PageState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
